<script lang="ts">
  import { onDestroy, onMount } from "svelte"
  import {
    registerEventCallback,
    unregisterEventCallback,
  } from "../../../../../_shared/_logic/eventbus"
  import LoadingPlaceholder from "./_modules/loading-placeholder"
  import type { OrgaViewFrameContext } from "../../../../context"
  import type { PageContext } from "../../_shared/_logic/customer-registry-index"
  import AddressTable from "./_modules/address-table"
  import { fetchOrganizationBusinessProfile } from "../../../../../../api/kehrblatt/business-profile"

  const { viewFrameContext } = $props<{
    viewFrameContext: OrgaViewFrameContext
  }>()
  const pageContext = $derived<PageContext>({
    organization: viewFrameContext.organization,
    canEditCustomers: !viewFrameContext.trialRestrictionsActive,
    isFreeTrialExperience: viewFrameContext.isFreeTrialExperience,
    trialRestrictionsActive: viewFrameContext.trialRestrictionsActive,
  })

  const organization = $derived(pageContext.organization)
  const organizationId = $derived(organization?.id ?? "")
  const canEdit = $derived(pageContext.canEditCustomers)

  let propertyLinks = $state<PropertyCustomer[]>([])
  let customers = $state<Customer[]>([])
  let dataRevision = $state(0)
  let defaultAddress = $state<{ zip?: string; city?: string; country?: string } | null>(null)

  const refreshAddresses = () => {
    if (!organization?.id) return

    propertyLinks = []
    customers = []
    dataRevision += 1
  }

  type AssistEventPayload = {
    name?: string
    normalizedName?: string
    parameters?: Record<string, unknown>
  }

  type CustomerRegistryChangedPayload = {
    organizationId?: string | null
    organization_id?: string | null
  }

  const loadDefaultAddress = async (targetOrganizationId: string) => {
    try {
      const response = await fetchOrganizationBusinessProfile(targetOrganizationId)
      const address = response.profile.address
      defaultAddress = {
        zip: address?.zip ?? undefined,
        city: address?.city ?? undefined,
        country: address?.country ?? undefined,
      }
    } catch (err) {
      console.error("customer_address_default_load_failed", err)
      defaultAddress = null
    }
  }

  const matchesOrganization = (payload: AssistEventPayload) => {
    const params = payload.parameters ?? {}
    const targetOrg =
      (params.organization_id as string | undefined) ??
      (params.organizationId as string | undefined) ??
      (params.org_id as string | undefined)

    if (targetOrg && organization?.id && targetOrg !== organization.id) {
      return false
    }

    const normalized = (payload.normalizedName ?? payload.name ?? "").toLowerCase()

    return normalized.includes("property") || normalized.includes("address")
  }

  let unregisterAssist: (() => void) | null = null
  onMount(() => {
    if (organization?.id) void loadDefaultAddress(organization.id)

    const handlerId = `addresses-${organization?.id ?? "org"}`
    const handler = (payload: AssistEventPayload) => {
      if (!matchesOrganization(payload)) return

      refreshAddresses()
    }
    registerEventCallback("assist.event", handlerId, handler)
    unregisterAssist = () => unregisterEventCallback("assist.event", handlerId)
  })
  onDestroy(() => unregisterAssist?.())

  let unregisterRegistryChange: (() => void) | null = null
  onMount(() => {
    const handlerId = `addresses-registry-${organization?.id ?? "org"}`
    const handler = (payload: CustomerRegistryChangedPayload = {}) => {
      const targetOrg = payload.organizationId ?? payload.organization_id

      if (targetOrg && organization?.id && targetOrg !== organization.id) return

      refreshAddresses()
    }
    registerEventCallback("customer-registry.changed", handlerId, handler)
    unregisterRegistryChange = () => unregisterEventCallback("customer-registry.changed", handlerId)
  })
  onDestroy(() => unregisterRegistryChange?.())
</script>

{#if organizationId}
  <AddressTable
    {propertyLinks}
    {organizationId}
    {customers}
    {canEdit}
    {defaultAddress}
    refreshKey={dataRevision}
    onChanged={(el: { properties: Property[]; links: PropertyCustomer[] }) => {
      propertyLinks = el.links
    }}
  />
{:else}
  <LoadingPlaceholder lines={4} />
{/if}
